import {
  AccountInputs,
  FinancialPlanAccounts,
  FinancialPlanData,
  GrowthTimelineResult,
  TimelineMilestone,
  TimelineYearPoint,
} from '../types/financialPlan';
import {
  annualRetirementSpendingAtAge,
  applyOnPaceInvestmentSurplus,
  computeAccountProjection,
  computeRequiredIncome,
  computeRetirementCorpus,
  computeYearCashflowAtAge,
  getSavingsBundleAtAge,
  debtLedgerAtAge,
  grossIncomeAtAge,
  grossIncomeTodayAtAge,
  k12SpendingAtAge,
  livingMonthlyAtPlanAge,
  surplusLumpSumToRetirementAtAge,
  yearsToRetirement,
} from './lifetimeGoalsCalc';
import { ACCOUNT_DISPLAY_LABELS } from './financialPlanDefaults';
import { distributeExtraToAccounts, lumpSumSplitByAccountWeight } from './lgcAccountRouting';
import { accountMonthlyContributionsAtAge } from './lgcAccountAllocation';
import { applyRothIraCapToMonthlyContributions } from './lgcRothIra';

export const ACCOUNT_KEYS: (keyof FinancialPlanAccounts)[] = ['k401', 'rothIra', 'hsa', 'taxable'];

export const ACCOUNT_CHART_COLORS: Record<keyof FinancialPlanAccounts, string> = {
  k401: '#2C3E2F',
  rothIra: '#3B82F6',
  hsa: '#DC2626',
  taxable: '#F59E0B',
};

export const ACCOUNT_CHART_LABELS: Record<keyof FinancialPlanAccounts, string> = {
  k401: ACCOUNT_DISPLAY_LABELS.k401,
  rothIra: ACCOUNT_DISPLAY_LABELS.rothIra,
  hsa: ACCOUNT_DISPLAY_LABELS.hsa,
  taxable: 'Taxable',
};

type AccountBalances = Record<keyof FinancialPlanAccounts, number>;

function emptyBalances(): AccountBalances {
  return { k401: 0, rothIra: 0, hsa: 0, taxable: 0 };
}

function startingBalances(accounts: FinancialPlanAccounts): AccountBalances {
  return {
    k401: Math.max(0, accounts.k401.balance),
    rothIra: Math.max(0, accounts.rothIra.balance),
    hsa: Math.max(0, accounts.hsa.balance),
    taxable: Math.max(0, accounts.taxable.balance),
  };
}

function mergeMonthlyContributions(
  base: Record<keyof FinancialPlanAccounts, number>,
  extra: Record<keyof FinancialPlanAccounts, number>,
): Record<keyof FinancialPlanAccounts, number> {
  const merged = {} as Record<keyof FinancialPlanAccounts, number>;
  ACCOUNT_KEYS.forEach((k) => {
    merged[k] = Math.max(0, base[k]) + Math.max(0, extra[k]);
  });
  return merged;
}

function advanceBalancesOneYear(
  balances: AccountBalances,
  accounts: FinancialPlanAccounts,
  defaultReturnPct: number,
  extraByAccount: Record<keyof FinancialPlanAccounts, number>,
  baseMonthlyByAccount: Record<keyof FinancialPlanAccounts, number>,
  age: number,
): AccountBalances {
  const combined = applyRothIraCapToMonthlyContributions(
    mergeMonthlyContributions(baseMonthlyByAccount, extraByAccount),
    age,
  );
  const next = {} as AccountBalances;
  ACCOUNT_KEYS.forEach((k) => {
    const rate = accounts[k].annualReturnPct ?? defaultReturnPct;
    const monthlyContribution = combined[k];
    next[k] = computeAccountProjection(
      {
        balance: balances[k],
        monthlyContribution,
        annualReturnPct: rate,
        ...(k === 'k401' ? { employerMatchPct: 0 } : {}),
      },
      1,
      rate,
    );
  });
  return next;
}

function totalBalance(b: AccountBalances): number {
  return ACCOUNT_KEYS.reduce((s, k) => s + b[k], 0);
}

export function simulateDrawdownYear(
  balances: AccountBalances,
  accounts: FinancialPlanAccounts,
  defaultReturnPct: number,
  annualWithdrawal: number,
): AccountBalances {
  const total = totalBalance(balances);
  if (total <= 0) return emptyBalances();

  const withdrawal = Math.min(Math.max(0, annualWithdrawal), total);
  const next = {} as AccountBalances;

  ACCOUNT_KEYS.forEach((k) => {
    const rate = pctToAccountRate(accounts[k], defaultReturnPct);
    const share = balances[k] / total;
    const afterGrowth = balances[k] * (1 + rate);
    next[k] = Math.max(0, afterGrowth - withdrawal * share);
  });

  return next;
}

function pctToAccountRate(account: AccountInputs, defaultReturnPct: number): number {
  return (account.annualReturnPct ?? defaultReturnPct) / 100;
}

function buildMilestones(
  plan: FinancialPlanData,
  currentAge: number,
  retirementAge: number,
  endAge: number,
): TimelineMilestone[] {
  const milestones: TimelineMilestone[] = [];

  plan.children.forEach((child) => {
    const k12StartAge = currentAge + Math.max(0, child.primaryStartAge - child.currentAge);
    const k12EndAge = currentAge + Math.max(0, child.highSchoolEndAge - child.currentAge);
    if (
      (child.primaryAnnualCostToday > 0 || child.highSchoolAnnualCostToday > 0) &&
      k12StartAge >= currentAge &&
      k12StartAge <= endAge
    ) {
      milestones.push({
        id: `k12-start-${child.id}`,
        age: k12StartAge,
        label: `${child.name} K-12 starts`,
        kind: 'k12Start',
      });
    }
    if (
      (child.primaryAnnualCostToday > 0 || child.highSchoolAnnualCostToday > 0) &&
      k12EndAge >= currentAge &&
      k12EndAge <= endAge
    ) {
      milestones.push({
        id: `k12-end-${child.id}`,
        age: k12EndAge,
        label: `${child.name} K-12 ends`,
        kind: 'k12End',
      });
    }

    const age = currentAge + Math.max(0, child.collegeStartAge - child.currentAge);
    if (age >= currentAge && age <= retirementAge) {
      milestones.push({
        id: `college-${child.id}`,
        age,
        label: `${child.name} starts college`,
        kind: 'college',
      });
    }
  });

  if (currentAge < 65 && endAge >= 65) {
    milestones.push({
      id: 'medicare',
      age: 65,
      label: 'Medicare eligible',
      kind: 'medicare',
    });
  }

  milestones.push({
    id: 'retirement',
    age: retirementAge,
    label: 'Target retirement',
    kind: 'retirement',
  });

  if (endAge > retirementAge) {
    milestones.push({
      id: 'plan-end',
      age: endAge,
      label: 'Plan end',
      kind: 'planEnd',
    });
  }

  return milestones.sort((a, b) => a.age - b.age);
}

function pointFromBalances(
  age: number,
  phase: TimelineYearPoint['phase'],
  current: AccountBalances,
  onTrack: AccountBalances,
  annualWithdrawal: number,
  cashflow?: {
    grossIncome: number;
    grossIncomeToday: number;
    debtServiceAnnual: number;
    k12SpendingAnnual: number;
    availableForSavings: number;
    requiredSaveMonthly: number;
    requiredSaveAnnual: number;
    requiredSpendAnnual: number;
    takeHomeAnnual: number;
    surplusAnnual: number;
    requiredGrossAnnual: number;
    saveRetirementAnnual?: number;
    saveOtherAnnual?: number;
    retirementInvestAnnual?: number;
    surplusToRetirementAnnual?: number;
    retireContribK401Annual?: number;
    retireContribRothAnnual?: number;
    retireContribHsaAnnual?: number;
    retireContribTaxableAnnual?: number;
    surplusLumpK401Annual?: number;
    surplusLumpRothAnnual?: number;
    surplusLumpHsaAnnual?: number;
    surplusLumpTaxableAnnual?: number;
  },
): TimelineYearPoint {
  return {
    age,
    phase,
    k401: current.k401,
    rothIra: current.rothIra,
    hsa: current.hsa,
    taxable: current.taxable,
    totalCurrent: totalBalance(current),
    onTrackK401: onTrack.k401,
    onTrackRothIra: onTrack.rothIra,
    onTrackHsa: onTrack.hsa,
    onTrackTaxable: onTrack.taxable,
    totalOnTrack: totalBalance(onTrack),
    annualWithdrawal,
    grossIncome: cashflow?.grossIncome,
    grossIncomeToday: cashflow?.grossIncomeToday,
    debtServiceAnnual: cashflow?.debtServiceAnnual,
    k12SpendingAnnual: cashflow?.k12SpendingAnnual,
    availableForSavings: cashflow?.availableForSavings,
    requiredSaveMonthly: cashflow?.requiredSaveMonthly,
    requiredSaveAnnual: cashflow?.requiredSaveAnnual,
    requiredSpendAnnual: cashflow?.requiredSpendAnnual,
    takeHomeAnnual: cashflow?.takeHomeAnnual,
    surplusAnnual: cashflow?.surplusAnnual,
    requiredGrossAnnual: cashflow?.requiredGrossAnnual,
    saveRetirementAnnual: cashflow?.saveRetirementAnnual,
    saveOtherAnnual: cashflow?.saveOtherAnnual,
    retirementInvestAnnual: cashflow?.retirementInvestAnnual,
    surplusToRetirementAnnual: cashflow?.surplusToRetirementAnnual,
    retireContribK401Annual: cashflow?.retireContribK401Annual,
    retireContribRothAnnual: cashflow?.retireContribRothAnnual,
    retireContribHsaAnnual: cashflow?.retireContribHsaAnnual,
    retireContribTaxableAnnual: cashflow?.retireContribTaxableAnnual,
    surplusLumpK401Annual: cashflow?.surplusLumpK401Annual,
    surplusLumpRothAnnual: cashflow?.surplusLumpRothAnnual,
    surplusLumpHsaAnnual: cashflow?.surplusLumpHsaAnnual,
    surplusLumpTaxableAnnual: cashflow?.surplusLumpTaxableAnnual,
  };
}

function configuredInvestmentMonthly(
  baseMonthly: Record<keyof FinancialPlanAccounts, number>,
): number {
  return baseMonthly.k401 + baseMonthly.rothIra + baseMonthly.hsa + baseMonthly.taxable;
}

/** Extra monthly retirement investing applied on the goal path at a plan age. */
export function onPaceExtraMonthlyAtAge(
  plan: FinancialPlanData,
  age: number,
  globalRetCatchUp: number,
): number {
  const baseMonthly = accountMonthlyContributionsAtAge(plan, age);
  const configuredInvestMo = configuredInvestmentMonthly(baseMonthly);
  const yearGapMo = Math.max(0, getSavingsBundleAtAge(plan, age).retirement - configuredInvestMo);
  return globalRetCatchUp > 0 ? globalRetCatchUp : yearGapMo;
}

export function computeGrowthTimeline(plan: FinancialPlanData): GrowthTimelineResult {
  const { profile, accounts, retirement, income, children } = plan;
  const currentAge = profile.currentAge;
  const retirementAge = profile.retirementAge;
  const yearsInRetirement = Math.max(0, retirement.yearsInRetirement);
  const endAge = retirementAge + yearsInRetirement;
  const yearsToRetire = yearsToRetirement(currentAge, retirementAge);
  const returnPct = profile.portfolioReturnPct;
  const taxRate = profile.effectiveTaxRatePct / 100;

  const incomeResult = computeRequiredIncome(plan);
  const corpusNeeded = computeRetirementCorpus(plan.profile, plan.retirement);
  const globalRetCatchUp =
    incomeResult.breakdown.find((b) => b.id === 'retirement')?.monthlyAmount ?? 0;
  const zeroExtra = distributeExtraToAccounts(plan, 0);

  const points: TimelineYearPoint[] = [];
  let currentBal = startingBalances(accounts);
  let onTrackBal = startingBalances(accounts);

  for (let year = 0; year <= yearsToRetire; year++) {
    const age = currentAge + year;
    const grossIncomeToday = grossIncomeTodayAtAge(profile, income, age);
    const grossIncome = grossIncomeAtAge(profile, income, age);
    const debtLedger = debtLedgerAtAge(plan.debts, profile, age);
    const k12SpendingAnnual = k12SpendingAtAge(children, profile, age);
    const livingAnnual = livingMonthlyAtPlanAge(plan, age) * 12;
    const takeHome = grossIncome * (1 - taxRate);
    const availableForSavings = Math.max(
      0,
      takeHome - livingAnnual - debtLedger.annualService - k12SpendingAnnual,
    );
    const yearCashflow = computeYearCashflowAtAge(plan, age, 'accumulation');
    const savings = getSavingsBundleAtAge(plan, age);
    const baseMonthly = accountMonthlyContributionsAtAge(plan, age);
    const configuredInvestMo = configuredInvestmentMonthly(baseMonthly);
    const onPaceExtraMo = onPaceExtraMonthlyAtAge(plan, age, globalRetCatchUp);
    const onPaceExtra = distributeExtraToAccounts(plan, onPaceExtraMo);
    const combinedMonthly = applyRothIraCapToMonthlyContributions(
      mergeMonthlyContributions(baseMonthly, onPaceExtra),
      age,
    );
    const saveRetirementAnnual = savings.retirement * 12;
    const saveOtherAnnual =
      (savings.college + savings.k12 + savings.health + savings.lifestyle) * 12;
    const retirementInvestAnnual = (configuredInvestMo + onPaceExtraMo) * 12;
    const surplusToRetirementAnnual = surplusLumpSumToRetirementAtAge(plan, onTrackBal, age);
    const lumpSplit = lumpSumSplitByAccountWeight(plan, surplusToRetirementAnnual, {
      age,
      rothContributedAnnual: combinedMonthly.rothIra * 12,
    });

    points.push(
      pointFromBalances(age, 'accumulation', currentBal, onTrackBal, 0, {
        grossIncome,
        grossIncomeToday,
        debtServiceAnnual: debtLedger.annualService,
        k12SpendingAnnual,
        availableForSavings,
        requiredSaveMonthly: yearCashflow.requiredSaveMonthly,
        requiredSaveAnnual: yearCashflow.requiredSaveAnnual,
        requiredSpendAnnual: yearCashflow.requiredSpendAnnual,
        takeHomeAnnual: yearCashflow.takeHomeAnnual,
        surplusAnnual: yearCashflow.surplusAnnual,
        requiredGrossAnnual: yearCashflow.requiredGrossAnnual,
        saveRetirementAnnual,
        saveOtherAnnual,
        retirementInvestAnnual,
        surplusToRetirementAnnual,
        retireContribK401Annual: combinedMonthly.k401 * 12,
        retireContribRothAnnual: combinedMonthly.rothIra * 12,
        retireContribHsaAnnual: combinedMonthly.hsa * 12,
        retireContribTaxableAnnual: combinedMonthly.taxable * 12,
        surplusLumpK401Annual: lumpSplit.k401,
        surplusLumpRothAnnual: lumpSplit.rothIra,
        surplusLumpHsaAnnual: lumpSplit.hsa,
        surplusLumpTaxableAnnual: lumpSplit.taxable,
      }),
    );

    if (year < yearsToRetire) {
      const yearAge = age;
      const baseMonthlyAdvance = accountMonthlyContributionsAtAge(plan, yearAge);
      const configuredInvestMoAdvance = configuredInvestmentMonthly(baseMonthlyAdvance);
      const yearGapMo = Math.max(0, getSavingsBundleAtAge(plan, yearAge).retirement - configuredInvestMoAdvance);
      const onPaceExtraMoAdvance = globalRetCatchUp > 0 ? globalRetCatchUp : yearGapMo;
      const onPaceExtraAdvance = distributeExtraToAccounts(plan, onPaceExtraMoAdvance);

      currentBal = advanceBalancesOneYear(currentBal, accounts, returnPct, zeroExtra, baseMonthlyAdvance, yearAge);
      onTrackBal = advanceBalancesOneYear(onTrackBal, accounts, returnPct, onPaceExtraAdvance, baseMonthlyAdvance, yearAge);
      if (globalRetCatchUp > 0 || onPaceExtraMoAdvance <= 0) {
        onTrackBal = applyOnPaceInvestmentSurplus(plan, onTrackBal, yearAge, onPaceExtraAdvance);
      }
    }
  }

  if (points.length === 0) {
    points.push(pointFromBalances(currentAge, 'accumulation', emptyBalances(), emptyBalances(), 0));
    currentBal = emptyBalances();
    onTrackBal = emptyBalances();
  }

  const currentFundingRatio =
    corpusNeeded > 0 ? Math.min(1, totalBalance(currentBal) / corpusNeeded) : 1;
  const onPaceFundingRatio =
    corpusNeeded > 0 ? Math.min(1, totalBalance(onTrackBal) / corpusNeeded) : 1;

  for (let y = 1; y <= yearsInRetirement; y++) {
    const age = retirementAge + y;
    const plannedSpending = annualRetirementSpendingAtAge(profile, retirement, age);
    const currentWithdrawal = plannedSpending * currentFundingRatio;
    const onPaceWithdrawal = plannedSpending * onPaceFundingRatio;
    currentBal = simulateDrawdownYear(currentBal, accounts, returnPct, currentWithdrawal);
    onTrackBal = simulateDrawdownYear(onTrackBal, accounts, returnPct, onPaceWithdrawal);
    const drawdownCashflow = computeYearCashflowAtAge(plan, age, 'drawdown', onPaceWithdrawal);

    points.push(
      pointFromBalances(age, 'drawdown', currentBal, onTrackBal, currentWithdrawal, {
        grossIncome: 0,
        grossIncomeToday: 0,
        debtServiceAnnual: 0,
        k12SpendingAnnual: 0,
        availableForSavings: 0,
        requiredSaveMonthly: 0,
        requiredSaveAnnual: 0,
        requiredSpendAnnual: drawdownCashflow.requiredSpendAnnual,
        takeHomeAnnual: drawdownCashflow.takeHomeAnnual,
        surplusAnnual: 0,
        requiredGrossAnnual: 0,
      }),
    );
  }

  const atRetire = points.find((p) => p.age === retirementAge);
  const currentAtRetire = atRetire?.totalCurrent ?? 0;
  const onPaceAtRetire = atRetire?.totalOnTrack ?? 0;
  const retirementFundedPctAccounts =
    corpusNeeded > 0 ? Math.min(100, (currentAtRetire / corpusNeeded) * 100) : 100;
  const retirementFundedPctOnPace =
    corpusNeeded > 0 ? Math.min(100, (onPaceAtRetire / corpusNeeded) * 100) : 100;

  return {
    points,
    milestones: buildMilestones(plan, currentAge, retirementAge, endAge),
    incomePhases: income.phases,
    corpusNeeded,
    retirementFundedPctAccounts,
    retirementFundedPctOnPace,
    yearsToRetire,
    yearsInRetirement,
    currentAge,
    retirementAge,
    endAge,
    inflationPct: profile.inflationPct,
    effectiveTaxRatePct: profile.effectiveTaxRatePct,
    birthDate: profile.birthDate,
    planStartDate: profile.planStartDate,
  };
}

/** Balances at target retirement age from the growth timeline simulation. */
export function retirementProjectionFromTimeline(timeline: GrowthTimelineResult): {
  currentAtRetire: number;
  onPaceAtRetire: number;
} {
  const point = timeline.points.find((p) => p.age === timeline.retirementAge);
  return {
    currentAtRetire: point?.totalCurrent ?? 0,
    onPaceAtRetire: point?.totalOnTrack ?? 0,
  };
}
